import {
  alertSelector,
  editButtonSelector,
  removeButtonSelector,
} from './fieldControllerFocus';
import {
  getCloseModalDelay,
  hasError,
  justClosedModal,
  transactionJustFailed,
} from './fieldControllerPredicates';

export const FIELD_UPDATE_EFFECTS = {
  SCHEDULE_CLOSE_TIMER: 'SCHEDULE_CLOSE_TIMER',
  CLEAR_CLOSE_TIMER: 'CLEAR_CLOSE_TIMER',
  CLOSE_MODAL: 'CLOSE_MODAL',
  FOCUS: 'FOCUS',
  FOCUS_ALERT_WHEN_NO_MODAL: 'FOCUS_ALERT_WHEN_NO_MODAL',
  SUCCESS_CALLBACK: 'SUCCESS_CALLBACK',
};

/**
 * The field controller's update logic as data. Given the previous and current
 * view state it returns the ordered list of side effects to run — no
 * dispatching, no DOM access, no timers — so every branch can be asserted on
 * without rendering anything.
 *
 * `prev` and `next` are both shaped:
 *   { fieldName, transaction, transactionRequest, showEditView,
 *     showRemoveModal, showValidationView, showUpdateSuccessAlert,
 *     showErrorAlert, forceEditView, useVapSvcShowErrors, hasSuccessCallback }
 *
 * `isModalOpen` is the one DOM read the logic needs; the caller supplies it.
 * The class read it lazily inside the branch that uses it, but nothing between
 * the start of the update and that branch touches the DOM, so reading it up
 * front is equivalent.
 */
export const getFieldUpdateEffects = ({ prev, next, isModalOpen = false }) => {
  const effects = [];
  const { fieldName } = next;

  // Exit the edit view if the update/save transaction takes too long to
  // resolve, so the Profile can show a "we're saving your new information..."
  // message instead of a stalled form.
  if (!prev.transaction && next.transaction) {
    effects.push({
      type: FIELD_UPDATE_EFFECTS.SCHEDULE_CLOSE_TIMER,
      delay: getCloseModalDelay(),
    });
  }

  // Keep the success alert on screen — a user who updated their mailing
  // address from the home address flow would otherwise see it disappear.
  if (next.showUpdateSuccessAlert) {
    effects.push({ type: FIELD_UPDATE_EFFECTS.CLEAR_CLOSE_TIMER });
  }

  const justFailed = transactionJustFailed(prev, next);

  // Do not auto-exit the edit view if the transaction failed.
  if (justFailed) {
    effects.push({ type: FIELD_UPDATE_EFFECTS.CLEAR_CLOSE_TIMER });
  }

  // Exit the remove modal if the delete transaction failed.
  if (prev.showRemoveModal && next.showRemoveModal && justFailed) {
    effects.push(
      { type: FIELD_UPDATE_EFFECTS.CLEAR_CLOSE_TIMER },
      { type: FIELD_UPDATE_EFFECTS.CLOSE_MODAL },
    );
  }

  const alertAppeared =
    (!prev.showUpdateSuccessAlert && next.showUpdateSuccessAlert) ||
    (!prev.showErrorAlert && next.showErrorAlert);

  if (justClosedModal(prev, next)) {
    effects.push({ type: FIELD_UPDATE_EFFECTS.CLEAR_CLOSE_TIMER });

    if (next.showErrorAlert || next.showUpdateSuccessAlert) {
      // Focus whichever alert is showing for this field. Gated on modal state
      // so focus isn't pulled off an open modal (e.g. the copy address flow).
      effects.push({
        type: FIELD_UPDATE_EFFECTS.FOCUS_ALERT_WHEN_NO_MODAL,
        selector: alertSelector(fieldName),
      });

      if (
        next.forceEditView &&
        next.hasSuccessCallback &&
        next.showUpdateSuccessAlert
      ) {
        effects.push({ type: FIELD_UPDATE_EFFECTS.SUCCESS_CALLBACK });
      }
    } else if (!next.forceEditView) {
      // forceEditView renders no edit button, so there is nothing to return
      // focus to.
      effects.push(
        prev.showRemoveModal && !next.showRemoveModal
          ? {
              type: FIELD_UPDATE_EFFECTS.FOCUS,
              selector: removeButtonSelector(fieldName),
              internalSelector: 'button',
            }
          : {
              type: FIELD_UPDATE_EFFECTS.FOCUS,
              selector: editButtonSelector(fieldName),
              internalSelector: 'button',
            },
      );
    }
  } else if (!isModalOpen && alertAppeared) {
    // A success or error alert appeared after a modal closed during a pending
    // transaction.
    effects.push({
      type: FIELD_UPDATE_EFFECTS.FOCUS,
      selector: alertSelector(fieldName),
    });

    if (
      next.forceEditView &&
      next.hasSuccessCallback &&
      next.showUpdateSuccessAlert &&
      !prev.showUpdateSuccessAlert
    ) {
      effects.push({ type: FIELD_UPDATE_EFFECTS.SUCCESS_CALLBACK });
    }
  } else if (
    next.forceEditView &&
    next.hasSuccessCallback &&
    prev.transactionRequest &&
    !next.transactionRequest
  ) {
    // The transaction request cleared. Report success for the non-address
    // fields, but not when that request had errored.
    if (
      next.useVapSvcShowErrors &&
      hasError(prev.transaction, prev.transactionRequest)
    ) {
      return effects;
    }

    effects.push({ type: FIELD_UPDATE_EFFECTS.SUCCESS_CALLBACK });
  }

  return effects;
};
